import { formaterJour, projetCreation } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  corps,
  ecrit,
  lire,
  type ProjetListe,
  requeteMoi,
  requetes,
  type StatutProjet,
} from '../api'
import { Formulaire } from '../composants/Formulaire'
import { formaterInstant } from '../composants/Historique'
import { Vide } from '../composants/Section'
import { EntetePage, Onglets } from '../composants/Ui'
import { LIBELLES_STATUT_PROJET } from '../libelles'

// Projets : un ensemble de tâches traité à part de la liste des tâches (un bilan, vendre la
// voiture), avec son objectif écrit à plusieurs, ses sous-pages et ses commentaires.

type Vue = StatutProjet | 'tous'

export function Projets() {
  const [vue, setVue] = useState<Vue>('en_cours')
  const [ajout, setAjout] = useState(false)
  const tous = useQuery(requetes.projets())
  const liste = (tous.data ?? []).filter((p) => vue === 'tous' || p.statut === vue)
  const compte = (s: StatutProjet) => tous.data?.filter((p) => p.statut === s).length
  return (
    <main>
      <EntetePage
        surtitre="Suivi"
        titre="Projets"
        action={{ icone: 'add', libelle: 'Nouveau projet', onClick: () => setAjout(true) }}
      />
      <div className="pile">
        <div className="barre-outils">
          <Onglets
            valeur={vue}
            onChange={setVue}
            defilants
            onglets={[
              {
                valeur: 'en_cours',
                libelle: 'En cours',
                icone: 'rocket_launch',
                compte: compte('en_cours'),
              },
              {
                valeur: 'termine',
                libelle: 'Terminés',
                icone: 'check_circle',
                compte: compte('termine'),
              },
              {
                valeur: 'abandonne',
                libelle: 'Abandonnés',
                icone: 'cancel',
                compte: compte('abandonne'),
              },
              { valeur: 'tous', libelle: 'Tous', icone: 'view_list' },
            ]}
          />
          <p className="discret">
            Les tâches d’un projet n’apparaissent pas dans Tâches, sauf celles qu’on y montre
            (l’œil, dans le projet).
          </p>
        </div>
        {ajout && <NouveauProjet fermer={() => setAjout(false)} />}
        {tous.isPending && <Vide>Chargement…</Vide>}
        {tous.data && liste.length === 0 && (
          <section className="carte">
            <Vide>Aucun projet ici.</Vide>
          </section>
        )}
        <div className="grille-projets">
          {liste.map((p) => (
            <CarteProjet key={p.id} p={p} />
          ))}
        </div>
      </div>
    </main>
  )
}

/** Du … au … ; une fin seule, un début seul ; rien sans dates. */
export function periodeProjet(p: { dateDebut: string | null; dateFin: string | null }) {
  if (p.dateDebut && p.dateFin)
    return `du ${formaterJour(p.dateDebut)} au ${formaterJour(p.dateFin)}`
  if (p.dateFin) return `jusqu’au ${formaterJour(p.dateFin)}`
  if (p.dateDebut) return `depuis le ${formaterJour(p.dateDebut)}`
  return null
}

function CarteProjet({ p }: { p: ProjetListe }) {
  const { faites, total } = p.avancement
  const periode = periodeProjet(p)
  return (
    <Link to="/projets/$id" params={{ id: p.id }} className="carte carte-lien carte-projet">
      <div className="carte-projet-tete">
        <span className="badge">{p.entite ?? 'Sans entité'}</span>
        {p.statut !== 'en_cours' && (
          <span className="badge">{LIBELLES_STATUT_PROJET[p.statut]}</span>
        )}
        {p.nouvelles > 0 && (
          <span className="pastille-nouvelles" title="Tâches nouvelles pour vous">
            {p.nouvelles}
          </span>
        )}
      </div>
      <strong className="carte-projet-titre">{p.titre}</strong>
      {periode && <span className={p.depasse ? 'discret alerte' : 'discret'}>{periode}</span>}
      <div className="avancement" aria-label={`${faites} tâche(s) faite(s) sur ${total}`}>
        <span className="avancement-barre">
          <span style={{ width: total ? `${Math.round((faites / total) * 100)}%` : 0 }} />
        </span>
        <span className="mono">
          {faites}/{total}
        </span>
      </div>
      <span className="discret">Activité {formaterInstant(p.activiteLe)}</span>
    </Link>
  )
}

function NouveauProjet({ fermer }: { fermer: () => void }) {
  const client = useQueryClient()
  const naviguer = useNavigate()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites).data ?? []
  const ecrivables = entites.filter((e) => !e.archivee && moi && ecrit(moi, e.id))
  return (
    <section className="carte">
      <h2>Nouveau projet</h2>
      <Formulaire
        champs={[
          { nom: 'titre', libelle: 'Titre', type: 'texte', requis: true },
          {
            nom: 'entiteId',
            libelle: 'Entité',
            type: 'choix',
            requis: !moi?.administrateur,
            aide: moi?.administrateur
              ? 'Le projet est visible comme son entité ; sans entité : des seuls administrateurs.'
              : 'Le projet est visible comme son entité.',
            options: ecrivables.map((e) => ({ valeur: e.id, libelle: e.nom })),
          },
          { nom: 'dateDebut', libelle: 'Début', type: 'date' },
          { nom: 'dateFin', libelle: 'Fin', type: 'date' },
          {
            nom: 'objectif',
            libelle: 'Objectif',
            type: 'zone',
            lignes: 4,
            aide: 'Les intentions du projet, en quelques lignes (modifiable ensuite, à plusieurs).',
          },
        ]}
        libelleBouton="Créer le projet"
        onAnnuler={fermer}
        onValider={async (v) => {
          const p = await lire(
            api.projets.$post({
              json: corps(projetCreation, {
                ...v,
                objectif: (v.objectif as string | null) ?? undefined,
              }),
            }),
          )
          await client.invalidateQueries({ queryKey: ['projets'] })
          fermer()
          await naviguer({ to: '/projets/$id', params: { id: p.id } })
        }}
      />
    </section>
  )
}
